import { useState, useEffect, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { PieChart } from "react-minimal-pie-chart";
import { useApi } from "../apiHelpers";
import { useToast } from "../components/Toast/ToastContext";
import { DataTable } from "../components/table/DataTable";
import useFleetStore from "../stores/useFleetStore";
import { parseESCVersion, getESCFilterKey } from "../utils/esc-utils";
import { buttonClass } from '../components/ui/Button';
import { Field } from '../components/ui/Field';
import Tabs, { TabPanel } from '../components/ui/Tabs';
import SegTrack from '../components/ui/SegTrack';
import EmptyState from '../components/ui/EmptyState';
import LogoSpinner from '../components/ui/LogoSpinner';
import { Panel, StatTile } from '../components/ui/Panel';
import FadeIn from '../components/ui/FadeIn';

// Color palette for chart segments
const CHART_COLORS = [
  "#FFCC33", // primary/gold
  "#3B82F6", // blue
  "#10B981", // green
  "#F59E0B", // amber
  "#8B5CF6", // purple
  "#EC4899", // pink
  "#06B6D4", // cyan
  "#F97316", // orange
  "#6366F1", // indigo
  "#84CC16", // lime
];

// The sort-by choices. This page sorts its OWN array (below) rather than
// handing `sortable` to the table: the buttons are the surface's answer to
// "which order", and a header caret next to them would be a second one.
const SORT_OPTIONS = [
  { value: "count", label: "Drones" },
  { value: "newest", label: "Newest" },
  { value: "oldest", label: "Oldest" },
];

// Format date for display (parse as local date to avoid timezone shift)
const formatDate = (dateStr) => {
  if (!dateStr) return null;
  const [year, month, day] = dateStr.split("T")[0].split("-");
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

// Determine ESC color based on type and status
// - BLHELI = red (we want to phase out)
// - AM32 needing update = amber
// - AM32 up to date = green
// - Error states = red
// - Unknown = gray
const getESCColor = (display, status) => {
  // Error states
  if (status === 'error') return "#EF4444"; // red

  // BLHELI is always red (phasing out)
  if (display.startsWith('BLHELI')) return "#EF4444"; // red

  // AM32 - check if needs update
  if (display.startsWith('AM32')) {
    if (display.includes('Needs Update')) {
      return "#F59E0B"; // amber
    }
    return "#10B981"; // green - good to go
  }

  // Unknown/other
  return "#6B7280"; // gray
};

// Get CSS class for ESC status in table
const getESCTableClass = (display, status) => {
  if (status === 'error') return "text-red-400 font-semibold";
  if (display.startsWith('BLHELI')) return "text-red-400";
  if (display.startsWith('AM32')) {
    if (display.includes('Needs Update')) return "text-yellow-400";
    return "text-green-400";
  }
  return "text-on-surface-variant";
};

// Actions is manage-only, and `columnVisibility` is what gates it — one place
// for the capability, no `canManage &&` on a th and again on a td. Two frozen
// objects rather than a fresh one per render, which would invalidate tanstack's
// per-row visible-cell memo every pass.
const ACTIONS_ON = { actions: true };
const ACTIONS_OFF = { actions: false };

// ---------------------------------------------------------------------------
// The two tabbed tables' columns (the shared DataTable, one per tab).
//
// Module scope, per the DataTable contract: the arrays never change identity,
// so the open in-cell editor survives every keystroke instead of remounting and
// throwing the caret out. Everything volatile — the edit draft and its setters,
// the save/cancel handlers, the chart-color lookup, the fleet-filter jumps —
// arrives on the table's `meta` and is read at render time. Never wrap one of
// these cells in React.memo: it would skip the parent's render and read a
// stale meta.
//
// Neither table passes `sortable`. The autopilot list is ordered by this page's
// own Drones/Newest/Oldest buttons, which reorder the data prop; the ESC list
// is ordered by count so its rows and its chart's segments read in one order.
//
// Plain JavaScript file: object literals, and <DataTable> with no type
// argument. The generic exists for the .tsx consumers and costs this file
// nothing.
// ---------------------------------------------------------------------------

const AUTOPILOT_COLUMNS = [
  {
    // The row wears its chart segment's color, which is what ties the two
    // halves of this page together.
    id: "swatch",
    header: "",
    meta: { headerClass: "w-8" },
    cell: (info) => {
      const { getChartColor } = info.table.options.meta;
      const chartColor = getChartColor(info.row.original.git_hash);
      return chartColor ? (
        <span
          className="w-3 h-3 rounded-full inline-block"
          style={{ backgroundColor: chartColor }}
        />
      ) : null;
    },
  },
  {
    id: "git_hash",
    header: "Git Hash",
    cell: ({ row }) => (
      <a
        href={`https://github.com/Firefly-Drone-Shows/PX4-Autopilot/commit/${row.original.git_hash}`}
        target="_blank"
        rel="noopener noreferrer"
        className="group"
      >
        <code
          className="text-on-surface bg-surface-container-highest px-2 py-1 rounded text-xs font-mono
            group-hover:text-primary group-hover:bg-gray-750 transition-colors"
          title={row.original.git_hash}
        >
          {row.original.git_hash?.substring(0, 7) || "-"}
        </code>
      </a>
    ),
  },
  {
    id: "label",
    header: "Label",
    cell: (info) => {
      const { canManage, editingHash, editValue, setEditValue, handleKeyDown, saving } =
        info.table.options.meta;
      const item = info.row.original;
      return canManage && editingHash === item.git_hash ? (
        <Field
          type="text"
          size="sm"
          aria-label="Label"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={(e) => handleKeyDown(e, item.git_hash)}
          placeholder="e.g., v2.1.0 Production"
          autoFocus
          disabled={saving}
        />
      ) : (
        <span className={item.label ? "text-primary" : "text-on-surface-variant italic"}>
          {item.label || "No label"}
        </span>
      );
    },
  },
  {
    id: "release_date",
    header: "Date",
    cell: (info) => {
      const { canManage, editingHash, editDate, setEditDate, handleKeyDown, saving } =
        info.table.options.meta;
      const item = info.row.original;
      return canManage && editingHash === item.git_hash ? (
        <Field
          type="date"
          size="sm"
          aria-label="Release date"
          value={editDate}
          onChange={(e) => setEditDate(e.target.value)}
          onKeyDown={(e) => handleKeyDown(e, item.git_hash)}
          className="!w-auto"
          disabled={saving}
        />
      ) : (
        <span className={item.release_date ? "text-on-surface" : "text-on-surface-disabled"}>
          {formatDate(item.release_date) || "-"}
        </span>
      );
    },
  },
  {
    id: "drone_count",
    header: "Drones",
    meta: { align: "right", numeric: true },
    cell: (info) => {
      const { onOpenFirmware } = info.table.options.meta;
      const item = info.row.original;
      return item.drone_count > 0 ? (
        <button
          onClick={() => onOpenFirmware(item.git_hash)}
          className="text-on-surface-variant hover:text-primary transition-colors"
        >
          {item.drone_count}
        </button>
      ) : (
        <span className="text-on-surface-disabled">0</span>
      );
    },
  },
  {
    id: "actions",
    header: "Actions",
    meta: { align: "right", headerClass: "w-24" },
    cell: (info) => {
      const { editingHash, saving, handleSave, handleCancelEdit, handleEdit } =
        info.table.options.meta;
      const item = info.row.original;
      return editingHash === item.git_hash ? (
        <div className="flex gap-2 justify-end">
          <button
            onClick={() => handleSave(item.git_hash)}
            disabled={saving}
            className={buttonClass('quiet', 'sm')}
          >
            {saving ? "Saving..." : "Save"}
          </button>
          <button
            onClick={handleCancelEdit}
            disabled={saving}
            className={buttonClass('quiet', 'sm')}
          >
            Cancel
          </button>
        </div>
      ) : (
        <button
          onClick={() => handleEdit(item.git_hash, item.label, item.release_date)}
          className={buttonClass('quiet', 'sm')}
        >
          {item.label ? "Edit" : "Add Label"}
        </button>
      );
    },
  },
];

const ESC_COLUMNS = [
  {
    // The ESC table's rows ARE the chart's segments, so the color is already
    // on the row. The old markup looked it up by filterKey in the same array
    // it was iterating, which returned this row's own color.
    id: "swatch",
    header: "",
    meta: { headerClass: "w-8" },
    cell: ({ row }) =>
      row.original.color ? (
        <span
          className="w-3 h-3 rounded-full inline-block"
          style={{ backgroundColor: row.original.color }}
        />
      ) : null,
  },
  {
    id: "version",
    header: "Version",
    cell: ({ row }) => (
      <span className={getESCTableClass(row.original.title, row.original.status)}>
        {row.original.title}
      </span>
    ),
  },
  {
    id: "drones",
    header: "Drones",
    meta: { align: "right", numeric: true },
    cell: (info) => {
      const { onOpenEsc } = info.table.options.meta;
      const item = info.row.original;
      return item.filterKey ? (
        <button
          onClick={() => onOpenEsc(item.filterKey)}
          className="text-on-surface-variant hover:text-primary transition-colors"
        >
          {item.value}
        </button>
      ) : (
        <span className="text-on-surface-variant">{item.value}</span>
      );
    },
  },
  {
    id: "percentage",
    header: "%",
    meta: { align: "right", numeric: true, cellClass: "text-on-surface-variant" },
    cell: ({ row }) => `${row.original.percentage}%`,
  },
];

// A chart segment and its row light together, and the tone is the vessel's own
// hover step so the two are indistinguishable.
const HOVER_SYNC_ROW = "bg-surface-container-highest";

const FirmwareLabels = ({ canManage = false }) => {
  const api = useApi();
  const history = useHistory();
  const toast = useToast();
  const drones = useFleetStore((state) => state.drones);
  const [labels, setLabels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingHash, setEditingHash] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [editDate, setEditDate] = useState("");
  const [saving, setSaving] = useState(false);
  const [hoveredSegment, setHoveredSegment] = useState(null);
  const [hoveredEscSegment, setHoveredEscSegment] = useState(null);
  const [showNotReported, setShowNotReported] = useState(true);
  const [activeTab, setActiveTab] = useState("autopilot"); // "autopilot" or "esc"
  const [sortBy, setSortBy] = useState("count"); // "count", "newest", "oldest"

  // Prepare chart data from labels (top 9 + "Other" if more than 10, plus "Not Reported")
  const chartData = useMemo(() => {
    if (!labels.length || !drones.length) return [];

    // Filter to only items with drones, sort by count descending
    const withDrones = labels
      .filter(item => item.drone_count > 0)
      .sort((a, b) => b.drone_count - a.drone_count);

    const reportedCount = withDrones.reduce((sum, item) => sum + item.drone_count, 0);
    const notReportedCount = drones.length - reportedCount;
    const totalDrones = drones.length;

    if (totalDrones === 0) return [];

    const result = [];

    // If 9 or fewer firmware versions (leaving room for "Not Reported"), show all
    if (withDrones.length <= 9) {
      withDrones.forEach((item, index) => {
        result.push({
          title: item.label || item.git_hash?.substring(0, 7),
          value: item.drone_count,
          color: CHART_COLORS[index % CHART_COLORS.length],
          hash: item.git_hash,
          percentage: ((item.drone_count / totalDrones) * 100).toFixed(1),
        });
      });
    } else {
      // More than 9: show top 8 + "Other"
      const top8 = withDrones.slice(0, 8);
      const rest = withDrones.slice(8);
      const otherCount = rest.reduce((sum, item) => sum + item.drone_count, 0);

      top8.forEach((item, index) => {
        result.push({
          title: item.label || item.git_hash?.substring(0, 7),
          value: item.drone_count,
          color: CHART_COLORS[index],
          hash: item.git_hash,
          percentage: ((item.drone_count / totalDrones) * 100).toFixed(1),
        });
      });

      // Add "Other" segment
      result.push({
        title: `Other (${rest.length})`,
        value: otherCount,
        color: "var(--outline)",
        hash: null,
        key: "__other__", // Stable key for React reconciliation
        percentage: ((otherCount / totalDrones) * 100).toFixed(1),
        isOther: true,
      });
    }

    // Add "Not Reported" segment if there are drones without firmware and toggle is on
    if (notReportedCount > 0 && showNotReported) {
      result.push({
        title: "Not Reported",
        value: notReportedCount,
        color: "#374151", // gray-700
        hash: null,
        key: "__not_reported__", // Stable key for React reconciliation
        percentage: ((notReportedCount / totalDrones) * 100).toFixed(1),
        isNotReported: true,
      });
    }

    return result;
  }, [labels, drones, showNotReported]);

  // Calculate not reported count for the toggle label
  const notReportedCount = useMemo(() => {
    const reportedCount = labels
      .filter(item => item.drone_count > 0)
      .reduce((sum, item) => sum + item.drone_count, 0);
    return drones.length - reportedCount;
  }, [labels, drones]);

  const totalDrones = useMemo(() =>
    chartData.reduce((sum, item) => sum + item.value, 0),
    [chartData]
  );

  // Prepare ESC distribution data from drones
  const escChartData = useMemo(() => {
    if (!drones.length) return [];

    // Group drones by ESC filter key (type + version)
    const escCounts = {};
    let escNotReportedCount = 0;

    drones.forEach(drone => {
      const filterKey = getESCFilterKey(
        drone.esc_version_result,
        drone.esc_version_type,
        drone.esc_version_major,
        drone.esc_version_minor
      );
      const esc = parseESCVersion(
        drone.esc_version_result,
        drone.esc_version_type,
        drone.esc_version_major,
        drone.esc_version_minor
      );

      if (!filterKey) {
        escNotReportedCount++;
      } else {
        if (!escCounts[filterKey]) {
          escCounts[filterKey] = { count: 0, status: esc.status, display: esc.display };
        }
        escCounts[filterKey].count++;
      }
    });

    const total = drones.length;

    // Convert to array and sort by count
    const sorted = Object.entries(escCounts)
      .map(([filterKey, { count, status, display }]) => ({
        title: display,
        value: count,
        color: getESCColor(display, status),
        status,
        key: filterKey,
        filterKey, // Used for navigation
        percentage: ((count / total) * 100).toFixed(1),
      }))
      .sort((a, b) => b.value - a.value);

    // Add "Not Reported" if any (respects showNotReported toggle)
    if (escNotReportedCount > 0 && showNotReported) {
      sorted.push({
        title: "Not Reported",
        value: escNotReportedCount,
        color: "#374151", // gray-700
        status: "unknown",
        key: "__esc_not_reported__",
        filterKey: null,
        percentage: ((escNotReportedCount / total) * 100).toFixed(1),
        isNotReported: true,
      });
    }

    return sorted;
  }, [drones, showNotReported]);

  const escTotalDrones = useMemo(() =>
    escChartData.reduce((sum, item) => sum + item.value, 0),
    [escChartData]
  );

  const fetchLabels = async () => {
    try {
      const data = await api.get("/admin/firmware-labels");
      setLabels(data || []);
    } catch (error) {
      console.error("Error fetching firmware labels:", error);
      toast.error("Failed to load firmware labels");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLabels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEdit = (hash, currentLabel, currentDate) => {
    setEditingHash(hash);
    setEditValue(currentLabel || "");
    setEditDate(currentDate ? currentDate.split("T")[0] : "");
  };

  const handleSave = async (hash) => {
    if (!editValue.trim()) {
      // If empty, delete the label
      await handleDelete(hash);
      return;
    }

    // Validate date if provided
    if (editDate && isNaN(new Date(editDate).getTime())) {
      toast.error("Invalid date");
      return;
    }

    setSaving(true);
    try {
      await api.post("/admin/firmware-labels", {
        body: { gitHash: hash, label: editValue.trim(), releaseDate: editDate || null }
      });
      await fetchLabels();
      setEditingHash(null);
      setEditValue("");
      setEditDate("");
      toast.success("Label saved");
    } catch (error) {
      console.error("Error saving label:", error);
      toast.error("Failed to save label");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (hash) => {
    setSaving(true);
    try {
      await api.delete(`/admin/firmware-labels/${encodeURIComponent(hash)}`);
      await fetchLabels();
      setEditingHash(null);
      setEditValue("");
      setEditDate("");
      toast.success("Label removed");
    } catch (error) {
      console.error("Error deleting label:", error);
      toast.error("Failed to remove label");
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingHash(null);
    setEditValue("");
    setEditDate("");
  };

  const handleKeyDown = (e, hash) => {
    if (e.key === "Enter") {
      handleSave(hash);
    } else if (e.key === "Escape") {
      setEditingHash(null);
      setEditValue("");
      setEditDate("");
    }
  };

  // Sort labels based on sortBy state
  const sortedLabels = useMemo(() => {
    const sorted = [...labels];
    switch (sortBy) {
      case "newest":
        return sorted.sort((a, b) => {
          if (!a.release_date && !b.release_date) return b.drone_count - a.drone_count;
          if (!a.release_date) return 1;
          if (!b.release_date) return -1;
          return new Date(b.release_date) - new Date(a.release_date);
        });
      case "oldest":
        return sorted.sort((a, b) => {
          if (!a.release_date && !b.release_date) return b.drone_count - a.drone_count;
          if (!a.release_date) return 1;
          if (!b.release_date) return -1;
          return new Date(a.release_date) - new Date(b.release_date);
        });
      case "count":
      default:
        return sorted.sort((a, b) => b.drone_count - a.drone_count);
    }
  }, [labels, sortBy]);

  // The two jumps into the fleet list, one per table's Drones cell.
  const onOpenFirmware = (hash) =>
    history.push(`/fleet?firmware=${encodeURIComponent(hash)}`);
  const onOpenEsc = (filterKey) =>
    history.push(`/fleet?esc=${encodeURIComponent(filterKey)}`);

  if (loading) return <LogoSpinner label="Loading firmware versions" />;

  // Get chart color for a label item based on its position in chartData
  // (the label list and the chart are two different arrays, so this is a real
  // lookup — travels to the swatch cell on the table's `meta`).
  const getChartColor = (hash) => {
    const index = chartData.findIndex(item => item.hash === hash);
    return index >= 0 ? chartData[index].color : null;
  };

  return (
    // Rendered inside FirmwarePage, which provides the page header + tab bar.
    <>
      {/* Stats Overview */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        <StatTile label="Total Drones" value={drones.length} tone="accent" />
        <StatTile label="AP Versions" value={labels.filter(l => l.drone_count > 0).length} />
        <StatTile label="ESC Versions" value={escChartData.filter(e => !e.isNotReported).length} />
      </div>

      {/* Distribution Charts - Side by Side */}
      <Panel
        title="Distribution"
        className="mb-6"
        actions={
          notReportedCount > 0 && (
            <label className="flex items-center gap-2 text-xs text-on-surface-variant cursor-pointer select-none">
              <span>Show Not Reported</span>
              <button
                role="switch"
                aria-checked={showNotReported}
                onClick={() => setShowNotReported(!showNotReported)}
                className={`relative w-9 h-5 rounded-full transition-colors
                  ${showNotReported ? "bg-primary" : "bg-outline"}`}
              >
                <span
                  className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform
                    ${showNotReported ? "translate-x-4" : "translate-x-0"}`}
                />
              </button>
            </label>
          )
        }
      >
        <div className="flex flex-col sm:flex-row items-center justify-around gap-8">
          {/* Autopilot Chart */}
          {chartData.length > 0 && (
            <div className="flex flex-col items-center">
              <div
                className="relative w-40 h-40 flex-shrink-0"
                role="img"
                aria-label={`Autopilot firmware distribution chart showing ${totalDrones} drones`}
              >
                <PieChart
                  data={chartData}
                  lineWidth={35}
                  paddingAngle={2}
                  animate
                  animationDuration={500}
                  onMouseOver={(_, index) => setHoveredSegment(index)}
                  onMouseOut={() => setHoveredSegment(null)}
                  segmentsStyle={(index) => ({
                    cursor: chartData[index]?.hash ? "pointer" : "default",
                    opacity: hoveredSegment === null || hoveredSegment === index ? 1 : 0.5,
                    transition: "opacity 0.2s",
                  })}
                  onClick={(_, index) => {
                    if (index < 0 || index >= chartData.length) return;
                    const item = chartData[index];
                    if (item?.hash && !item.isOther && !item.isNotReported) {
                      history.push(`/fleet?firmware=${encodeURIComponent(item.hash)}`);
                    }
                  }}
                />
                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                  <span className="text-2xl font-bold text-white">AP</span>
                </div>
              </div>
              {/* Hover label */}
              <div className="h-6 mt-2 text-center w-40">
                {hoveredSegment !== null && chartData[hoveredSegment] && (
                  <span className="text-sm text-on-surface block truncate" title={chartData[hoveredSegment].title}>
                    {chartData[hoveredSegment].title}
                    <span className="text-on-surface-variant ml-1">
                      ({chartData[hoveredSegment].percentage}%)
                    </span>
                  </span>
                )}
              </div>
            </div>
          )}

          {/* ESC Chart */}
          {escChartData.length > 0 && (
            <div className="flex flex-col items-center">
              <div
                className="relative w-40 h-40 flex-shrink-0"
                role="img"
                aria-label={`ESC version distribution chart showing ${escTotalDrones} drones`}
              >
                <PieChart
                  data={escChartData}
                  lineWidth={35}
                  paddingAngle={2}
                  animate
                  animationDuration={500}
                  onMouseOver={(_, index) => setHoveredEscSegment(index)}
                  onMouseOut={() => setHoveredEscSegment(null)}
                  segmentsStyle={(index) => ({
                    cursor: escChartData[index]?.filterKey ? "pointer" : "default",
                    opacity: hoveredEscSegment === null || hoveredEscSegment === index ? 1 : 0.5,
                    transition: "opacity 0.2s",
                  })}
                  onClick={(_, index) => {
                    if (index < 0 || index >= escChartData.length) return;
                    const item = escChartData[index];
                    if (item?.filterKey && !item.isNotReported) {
                      history.push(`/fleet?esc=${encodeURIComponent(item.filterKey)}`);
                    }
                  }}
                />
                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                  <span className="text-2xl font-bold text-white">ESC</span>
                </div>
              </div>
              {/* Hover label */}
              <div className="h-6 mt-2 text-center w-40">
                {hoveredEscSegment !== null && escChartData[hoveredEscSegment] && (
                  <span
                    className={`text-sm block truncate ${getESCTableClass(escChartData[hoveredEscSegment].title, escChartData[hoveredEscSegment].status)}`}
                    title={escChartData[hoveredEscSegment].title}
                  >
                    {escChartData[hoveredEscSegment].title}
                    <span className="text-on-surface-variant ml-1">
                      ({escChartData[hoveredEscSegment].percentage}%)
                    </span>
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </Panel>

      {/* Tabbed Tables. No card around them: the shared table renders its own
          vessel — shell, radius, scroll container and the face on every row —
          so a box here would draw a second one around the real one. The tablist
          stays page chrome above the vessels because it governs BOTH of them,
          and its bottom rule is the same idiom FirmwarePage's own tab bar uses
          one level up. */}
      <Tabs
        label="Firmware type tabs"
        id="firmware"
        fill
        className="mb-4"
        value={activeTab}
        onChange={setActiveTab}
        tabs={[
          { key: "autopilot", label: "Autopilot", count: labels.length },
          { key: "esc", label: "ESCs", count: escChartData.filter(e => !e.isNotReported).length },
        ]}
      />

      {/* Autopilot Table. The sort buttons ride the vessel's working strip, so
          they sit on the pile they reorder instead of in a rule-divided bar of
          their own. The ORDER stays the page's: the buttons rewrite
          sortedLabels, and no `sortable` goes to the table.

          Empty state on the default `emptyMode` ('row'), not the page's old
          arrangement: that markup left the header standing and put a centered
          block directly under it, which is what a full-width row already is.
          Same node, same classes, one fewer element. */}
      <FadeIn key={activeTab}>
      {activeTab === "autopilot" && (
        <TabPanel id="firmware" value="autopilot">
          <DataTable
            data={sortedLabels}
            columns={AUTOPILOT_COLUMNS}
            getRowId={(item) => item.git_hash}
            columnVisibility={canManage ? ACTIONS_ON : ACTIONS_OFF}
            meta={{
              canManage,
              editingHash,
              editValue,
              editDate,
              saving,
              setEditValue,
              setEditDate,
              getChartColor,
              handleKeyDown,
              handleSave,
              handleEdit,
              handleCancelEdit,
              onOpenFirmware,
            }}
            strip={
              <>
                <span className="text-xs text-on-surface-variant">Sort by:</span>
                <SegTrack
                  label="Sort by"
                  size="sm"
                  value={sortBy}
                  onChange={setSortBy}
                  options={SORT_OPTIONS.map((o) => ({ key: o.value, label: o.label }))}
                />
              </>
            }
            rowProps={(item) => {
              // The chart segment this row belongs to. Hovering either one
              // lights both, and the handlers stay on the tr exactly where they
              // were: rowProps' leftover attributes are spread onto it.
              const chartIndex = chartData.findIndex(c => c.hash === item.git_hash);
              return {
                className: chartIndex >= 0 && hoveredSegment === chartIndex ? HOVER_SYNC_ROW : "",
                onMouseEnter: () => chartIndex >= 0 && setHoveredSegment(chartIndex),
                onMouseLeave: () => setHoveredSegment(null),
              };
            }}
            empty={
              <EmptyState variant="area" title="No firmware versions found in the database.">
                Firmware versions are populated when drones report telemetry.
              </EmptyState>
            }
          />
        </TabPanel>
      )}

      {/* ESC Table */}
      {activeTab === "esc" && (
        <TabPanel id="firmware" value="esc">
          <DataTable
            data={escChartData}
            columns={ESC_COLUMNS}
            getRowId={(item) => item.key}
            meta={{ onOpenEsc }}
            rowProps={(item) => {
              // hoveredEscSegment indexes escChartData, which is what the chart
              // renders — NOT the rendered row order — so the position comes off
              // the array itself rather than the row's display index.
              const index = escChartData.indexOf(item);
              return {
                className: hoveredEscSegment === index ? HOVER_SYNC_ROW : "",
                onMouseEnter: () => setHoveredEscSegment(index),
                onMouseLeave: () => setHoveredEscSegment(null),
              };
            }}
            empty={
              <EmptyState variant="area" title="No ESC versions found.">
                ESC versions are populated when drones report telemetry.
              </EmptyState>
            }
          />
        </TabPanel>
      )}
      </FadeIn>
    </>
  );
};

export default FirmwareLabels;
